// DocumentEditor — the one form behind invoices and estimates.
//
// It edits exactly the fields routes/invoices.js and routes/estimates.js
// accept and sends them as they are. It never computes a total: before a save
// the totals panel says "Save to calculate", after one it shows the figures
// the server wrote (subtotal, discount, tax, total). The XOR between per-line
// discounts and the whole-document discount is the server's rule
// (utils/lineItems.anyLineDiscount); here it only disables the side that would
// be ignored and says why.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataTable, FwButton, FwField, FwInput, FwSelect, FwTextarea, Notice, Pill, RECORD_CARD_MAX, SectionLabel, Stat } from '../../../ui/fw';
import { fmtMoney, fmtPct, todayIso } from './format';
import { estimatePill, invoicePill, needsPrice } from './status';
import { NO_CLIENT, clientKey, clientOptions, groupByClient } from './jobsByClient';
import './DocumentEditor.css';

let seq = 0;
const uid = () => `li-${Date.now().toString(36)}-${(seq += 1)}`;

export function blankItem() {
  return { key: uid(), description: '', quantity: 1, unit: '', unit_price: '', discount_amount: '', discount_type: 'fixed', notes: '', photos: [] };
}

function itemFromRow(it) {
  if (!it) return blankItem();
  return {
    key: uid(),
    id: it.id,
    description: it.description || '',
    quantity: it.quantity === null || it.quantity === undefined ? 1 : it.quantity,
    unit: it.unit || '',
    unit_price: it.unit_price === null || it.unit_price === undefined ? '' : it.unit_price,
    discount_amount: Number(it.discount_amount) > 0 ? it.discount_amount : '',
    discount_type: it.discount_type === 'percent' ? 'percent' : 'fixed',
    notes: it.notes || '',
    photos: Array.isArray(it.photos) ? it.photos : [],
  };
}

/** Form state from a server row (with items + linked_jobs), or a blank one. */
export function formFromDoc(doc) {
  const d = doc || {};
  const rows = Array.isArray(d.items) && d.items.length ? d.items : [null];
  return {
    client_id: d.client_id ? String(d.client_id) : '',
    client_name: d.client_name || '',
    client_email: d.client_email || '',
    client_phone: d.client_phone || '',
    client_address: d.client_address || '',
    title: d.title || '',
    description: d.description || '',
    notes: d.notes || '',
    due_date: d.due_date ? String(d.due_date).slice(0, 10) : '',
    deposit_amount: Number(d.deposit_amount) > 0 ? d.deposit_amount : '',
    payment_terms: d.payment_terms || '',
    discount_amount: Number(d.discount_amount) > 0 ? d.discount_amount : '',
    discount_type: d.discount_type === 'percent' ? 'percent' : 'fixed',
    discount_description: d.discount_description || '',
    // Blank means "leave the rate alone" — the server keeps the rate a document
    // was issued with, or resolves the client's default for a new one.
    tax_rate: '',
    expires_in_days: '',
    pull_job_content: true,
    job_ids: (Array.isArray(d.linked_jobs) ? d.linked_jobs : []).map((j) => Number(j.job_id !== undefined ? j.job_id : j.id)).filter(Number.isFinite),
    items: rows.map(itemFromRow),
  };
}

/** Mirrors utils/lineItems.anyLineDiscount on the server. */
export function anyLineDiscount(items) {
  return (items || []).some((it) => (Number(it.discount_amount) || 0) > 0);
}

export function validateForm(form) {
  const errors = {};
  if (!form.client_name || !form.client_name.trim()) errors.client_name = 'Pick a client, or type a name.';
  if (!form.items.length) errors.items = 'Add at least one line.';
  else if (form.items.some((it) => !it.description || !it.description.trim())) errors.items = 'Every line needs a description.';
  if (form.client_email && form.client_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.client_email.trim())) errors.client_email = 'That does not look like an email address.';
  return errors;
}

/** The request body — only fields the route reads, numbers as numbers. */
export function payloadFromForm(form, kind, { isNew = false } = {}) {
  const lineMode = anyLineDiscount(form.items);
  const items = form.items.map((it, i) => {
    const o = {
      description: String(it.description || '').trim(),
      quantity: Number(it.quantity) || 0,
      unit_price: Number(it.unit_price) || 0,
      discount_amount: lineMode ? (Number(it.discount_amount) || 0) : 0,
      discount_type: it.discount_type === 'percent' ? 'percent' : 'fixed',
      sort_order: i,
    };
    if (it.unit && String(it.unit).trim()) o.unit = String(it.unit).trim();
    if (it.notes && String(it.notes).trim()) o.notes = String(it.notes).trim();
    if (Array.isArray(it.photos) && it.photos.length) {
      // Sending the line's photos back is what keeps them: the server clears
      // and rewrites line items (and their photos) on every save.
      o.photos = it.photos.map((p) => ({ media_id: p.media_id || null, file_path: p.file_path || null, caption: p.caption || '' }));
    }
    return o;
  });
  const body = {
    client_id: form.client_id ? Number(form.client_id) : null,
    client_name: String(form.client_name || '').trim(),
    client_email: String(form.client_email || '').trim() || null,
    client_phone: String(form.client_phone || '').trim() || null,
    client_address: String(form.client_address || '').trim() || null,
    description: form.description || '',
    notes: form.notes || '',
    items,
    discount_amount: lineMode ? 0 : (Number(form.discount_amount) || 0),
    discount_type: form.discount_type === 'percent' ? 'percent' : 'fixed',
    discount_description: form.discount_description || null,
    job_ids: (form.job_ids || []).map(Number).filter(Number.isFinite),
  };
  if (form.tax_rate !== '' && form.tax_rate !== null && form.tax_rate !== undefined) body.tax_rate = Number(form.tax_rate) || 0;
  if (kind === 'invoice') {
    body.due_date = form.due_date || null;
    body.deposit_amount = Number(form.deposit_amount) || 0;
    body.payment_terms = form.payment_terms || null;
  }
  if (kind === 'estimate') {
    body.title = form.title || '';
    body.pull_job_content = !!form.pull_job_content;
    if (isNew && form.expires_in_days !== '') body.expires_in_days = Number(form.expires_in_days);
  }
  return body;
}

/**
 * What this document is worth, in the FIRST screenful.
 *
 * ── Measured in a real browser at 320px, as an admin, 2026-09-11 ───────────
 *
 *   /invoices/:id    "Total $510.00"   at y=3133 on a 3891px page
 *   /estimates/:id   "Total $4,200.00" at y=2893 on a 3626px page
 *
 * Both figures sit in TotalsPanel at the FOOT of the editor, so a person who
 * opened a bill on a phone to answer "how much is this one for" had to scroll
 * past the client block, the dates, the tax field, every line item, the
 * discount panel and the linked jobs — four screenfuls — to reach the single
 * number the document exists to state. The owner's words for exactly this
 * class of screen: "where do I see the hours, the amount in dollars?"
 *
 * ── This computes nothing, and that is the whole design ────────────────────
 *
 * It is TotalsPanel's headline figure HOISTED, not a second opinion about it.
 * `doc.total` and `doc.amount_paid` are columns the server wrote; they are
 * rendered through the same fmtMoney every other Office figure goes through.
 * There is no subtraction here — a phone-friendly "outstanding" would be
 * `total - amount_paid` computed in the client, which is money math and is
 * refused by CLAUDE.md rule 4. The breakdown (subtotal, discount, tax) stays
 * at the foot beside the lines that produce it; only the ANSWER moves up.
 *
 * The tone is the document's own status tone, read off shared/status.js
 * rather than restated here, so the two cannot drift: a paid invoice reads
 * green and an overdue one red. `hint` carries that status in WORDS, so the
 * strip still says what state the money is in when the colour is not
 * available to the reader — APP_REBUILD §3.3, colour plus text, never colour
 * alone.
 *
 * "Paid" appears only when something has actually been collected. A row
 * reading "Paid $0.00" on every draft would be the em-dash failure in another
 * costume: a slot spent saying nothing, on the screen where the slots are
 * scarcest.
 */
export function DocumentSummary({ doc, kind, today = todayIso() }) {
  if (!doc || doc.total === null || doc.total === undefined) return null;
  const pill = kind === 'estimate' ? estimatePill(doc) : invoicePill(doc, today);
  const collected = Number(doc.amount_paid) || 0;
  return (
    <div className="of-stats__row" data-summary={kind === 'estimate' ? 'estimate' : 'invoice'}>
      <Stat label="Total" value={fmtMoney(doc.total)} tone={pill.tone} hint={pill.label} data-summary-total />
      {kind === 'invoice' && collected > 0 ? (
        <Stat label="Paid" value={fmtMoney(doc.amount_paid)} tone="ok" data-summary-paid />
      ) : null}
    </div>
  );
}

/**
 * What the "Amount paid" slot says when no money has arrived.
 *
 * ── The em-dash failure, in its third costume ──────────────────────────────
 *
 * Measured in Chromium at 320px on 2026-09-13, on a twelve-line DRAFT invoice
 * — never sent, never paid — the totals panel ended:
 *
 *     Tax (0%)        $0.00
 *     Total       $4,824.00
 *     Amount paid     $0.00
 *
 * A draft has never been paid anything, so that row spends a line of the
 * scarcest screen in the product stating a fact about nothing. It is the same
 * shape DocumentSummary's header already names (a "Paid $0.00" row on every
 * draft) and the same shape screens/Invoices.js `dueLabel` already fixed for
 * the Due slot: replace the empty figure with what the STORED data actually
 * answers, rather than deleting the slot or inventing a number for it.
 *
 * ── Nothing is computed, and nothing is claimed ────────────────────────────
 *
 * Every branch reads one column the server wrote and says what it means in
 * words. `amount_paid` is the figure and is printed unchanged when there is
 * one (CLAUDE.md rule 4 — no arithmetic on money here, not even a subtraction
 * for a balance). The `status === 'paid'` branch exists because the two
 * columns can disagree on a legacy row: PUT /:id/mark-paid writes
 * `amount_paid = total` today, so a paid invoice reading $0.00 is a row from
 * before it did — and "Amount paid $0.00" on a settled invoice is the worst
 * version of this defect, not the mildest. It states the status, which is what
 * is actually known, and no figure at all.
 *
 * @returns {{figure: boolean, said: string|null, state: string}}
 */
export function paidLabel(invoice) {
  const inv = invoice || {};
  if ((Number(inv.amount_paid) || 0) > 0) return { figure: true, said: null, state: 'collected' };
  if (inv.status === 'paid') return { figure: false, said: 'Marked paid', state: 'settled' };
  if (inv.sent_at) return { figure: false, said: 'Nothing received yet', state: 'awaiting' };
  return { figure: false, said: 'Not sent yet', state: 'unsent' };
}

/**
 * Only what the server returned. `pending` = there are unsaved edits.
 *
 * ── The discount that was applied and never named ──────────────────────────
 *
 * Measured in a browser at 390px on 2026-09-13, on an invoice with two
 * discounted lines and one at full price:
 *
 *   Subtotal      $3,628.50
 *   Tax (6.35%)     $221.36
 *   Total         $3,707.36        and 3628.50 + 221.36 is 3849.86
 *
 * $142.50 had come off the bill and this panel had no row for it; the same
 * run on an estimate lost $1,344.00. The cause is the XOR, not a rounding
 * slip: when ANY line carries a discount the server zeroes the document's own
 * `discount_amount` (routes/invoices.js, `if (lineDiscountMode)`) and keeps
 * `subtotal` GROSS (utils/discountMath.js computeTotals), so the old test —
 * `Number(doc.discount_amount) > 0` — was false on exactly the documents that
 * have the larger discount.
 *
 * ── Why there is no FIGURE on the new row, and must not be ─────────────────
 *
 * `computeTotals` produces `lineDiscountTotal` and the routes throw it away:
 * no column stores it, no response carries it. The only way to print
 * "$142.50" here is to sum quantity x unit_price x discount in the client,
 * which is money math and is refused by CLAUDE.md rule 4. So this states that
 * a discount was taken and where to read it — the per-line amounts are on the
 * lines above, in both layouts — and computes nothing. Naming the gap is a
 * strictly better answer than a panel whose own rows do not add up; naming it
 * with a number this screen invented would be a worse one.
 *
 * `lineMode` mirrors the server's precedence exactly (computeTotals: a line
 * discount wins over the document one), which is also why `hasDiscount` now
 * requires its absence — a legacy row carrying both would otherwise draw a
 * document discount the server did not apply.
 */
export function TotalsPanel({ doc, kind, pending }) {
  if (pending || !doc) {
    return <div className="of-totals of-totals--pending" data-pending="true">Save to calculate</div>;
  }
  const lineMode = anyLineDiscount(doc.items);
  const hasDiscount = !lineMode && Number(doc.discount_amount) > 0;
  const discountLabel = doc.discount_type === 'percent' ? `Discount (${doc.discount_amount}%)` : 'Discount';
  const discountValue = doc.discount_type === 'percent' ? `${doc.discount_amount}%` : fmtMoney(doc.discount_amount);
  return (
    <dl className="of-totals" data-pending="false">
      <div className="of-totals__row">
        <dt>{lineMode ? 'Subtotal (before line discounts)' : 'Subtotal'}</dt>
        <dd className="fw-num">{fmtMoney(doc.subtotal)}</dd>
      </div>
      {lineMode ? (
        <div className="of-totals__row of-totals__row--said" data-line-discounts="true">
          <dt>Line discounts</dt>
          <dd>taken off the lines above</dd>
        </div>
      ) : null}
      {hasDiscount ? (
        <div className="of-totals__row"><dt>{discountLabel}{doc.discount_description ? ` — ${doc.discount_description}` : ''}</dt><dd className="fw-num">{discountValue}</dd></div>
      ) : null}
      <div className="of-totals__row"><dt>Tax ({fmtPct(doc.tax_rate)})</dt><dd className="fw-num">{fmtMoney(doc.tax_amount)}</dd></div>
      <div className="of-totals__row of-totals__row--total"><dt>Total</dt><dd className="fw-num">{fmtMoney(doc.total)}</dd></div>
      {kind === 'invoice' ? (() => {
        const paid = paidLabel(doc);
        return paid.figure
          ? <div className="of-totals__row" data-paid-state={paid.state}><dt>Amount paid</dt><dd className="fw-num">{fmtMoney(doc.amount_paid)}</dd></div>
          // `--said` is the row shape this panel already uses for the line
          // discounts it may not put a figure on: a label and a sentence.
          : <div className="of-totals__row of-totals__row--said" data-paid-state={paid.state}><dt>Amount paid</dt><dd>{paid.said}</dd></div>;
      })() : null}
    </dl>
  );
}

function jobLabel(job) {
  if (!job) return '';
  const where = [job.client_name, job.property_name].filter(Boolean).join(' · ');
  return where ? `${job.title} — ${where}` : job.title;
}

/** Rows for the description box: two to start, one per line typed, capped at twelve (it scrolls past that). */
export function descriptionRows(text) {
  const lines = String(text || '').split('\n').length;
  return Math.min(12, Math.max(2, lines));
}

/** A job inside a client's list: the client is already the heading, so only the property follows the title. */
function jobInClient(job) {
  return job.property_name ? `${job.title} — ${job.property_name}` : job.title;
}

/**
 * What "Add a job" offers, and which client "Jobs for" shows.
 *
 * The owner, 2026-09-26, on this panel: "Where do I filter jobs by client!?
 * Have asked for this 100 times!" It listed every unbilled job in the
 * server's order — finished first, newest first — so one client's jobs were
 * scattered through everyone else's. Now:
 *
 *   jobsFor  follows the document's client until the biller picks another
 *            (`chosen` is null until then). A document with no client, or
 *            "Any client", shows every job grouped under its client, A–Z,
 *            "No client" last.
 *   options  the "Jobs for" list: every client that has a job to link, A–Z,
 *            plus the document's own client even when it has none, so the
 *            select can say so rather than silently show someone else's.
 */
export function linkChoices({ jobs, docClientId, docClientName, chosen }) {
  const all = jobs || [];
  const options = clientOptions(all);
  if (docClientId && !options.some((c) => c.id === String(docClientId))) {
    options.push({ id: String(docClientId), name: docClientName || `Client #${docClientId}` });
    options.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }
  const noClient = all.some((j) => clientKey(j) === 'none');
  // A chosen client the list can no longer offer — its last job was just
  // linked — goes back to following the document. Left as it was, the select
  // held a value with no <option>, painted blank, and "Add a job" said there
  // was nothing to link while other clients' jobs sat hidden (#711 review).
  const offered = (v) => v === '' || options.some((c) => c.id === v) || (v === 'none' && noClient);
  const pick = chosen !== null && chosen !== undefined && offered(String(chosen)) ? String(chosen) : null;
  const jobsFor = pick !== null ? pick : (docClientId ? String(docClientId) : '');
  const shown = jobsFor ? all.filter((j) => clientKey(j) === jobsFor) : all;
  return { jobsFor, options, noClient, shown, groups: jobsFor ? null : groupByClient(all) };
}

/** A sentence describing what GET /api/estimates/job-details/:id says will come across. */
export function describePull(details) {
  const pull = details && details.pull;
  if (!pull || !pull.item) return 'This will link the job. Nothing else comes across.';
  const item = pull.item;
  const qty = Number(item.quantity) || 0;
  const unit = item.unit ? ` ${item.unit}` : '';
  const notes = item.notes ? String(item.notes).split('\n').filter(Boolean).length : 0;
  const photos = Array.isArray(pull.photos) ? pull.photos.length : 0;
  const parts = [
    `1 line — ${item.description}, ${qty}${unit} at ${fmtMoney(0)} (needs a price)`,
    `${photos} photo${photos === 1 ? '' : 's'}`,
    `${notes} subtask${notes === 1 ? '' : 's'} as notes`,
  ];
  return `This will add: ${parts.join('; ')}.`;
}

/**
 * Is a measured width too narrow for the line-item TABLE?
 *
 * The number is RECORD_CARD_MAX, which is FW_PHONE_MAX — the same 768 that
 * usePhoneLayout, RecordList, DesktopShell.css and --fw-phone-max already
 * agree on. APP_REBUILD.md §6: two layouts, not eight breakpoints. A width of
 * 0 (a list in a closed Sheet, a tab panel not yet laid out) is not narrow;
 * treating it as narrow would make the shape depend on whether the pane had
 * been opened yet.
 */
export function isNarrowLineWidth(width) {
  return typeof width === 'number' && width > 0 && width < RECORD_CARD_MAX;
}

/**
 * Watch the line-item region's own width and say whether it must stack.
 *
 * CONTAINER width, not viewport width, and the distinction is the one
 * RecordList already makes for exactly this reason: the editor's own table
 * branch is `DataTable`, which measures its wrapper and flips to cards below
 * 768. If this hook asked the VIEWPORT instead, a 1000px desktop showing the
 * editor in a narrow pane would answer "wide" here, hand the seven columns to
 * DataTable, and DataTable — measuring the pane — would answer "narrow" and
 * draw the six-slot card this whole layout exists to replace. Measuring the
 * same box as DataTable is what keeps the two answers from disagreeing.
 *
 * Seeded from the viewport so the FIRST paint is already the right shape: a
 * ResizeObserver callback lands after paint, and a table flashing into a form
 * is the "browser stuffed into an app" feel the rebuild is removing.
 *
 * The fallback is `window.innerWidth` rather than matchMedia, and that is
 * deliberate: jsdom has no ResizeObserver, and its matchMedia is a stub that
 * answers `matches: false` at every width — a fallback built on it could never
 * be seen to switch in either direction, which CLAUDE.md rule 8 calls a hope
 * rather than a guard. innerWidth is settable in a test, so both directions
 * are provable, and DocumentEditor.test.js drives them.
 */
function useNarrowLines(ref) {
  const [narrow, setNarrow] = useState(() => (typeof window === 'undefined' ? false : isNarrowLineWidth(window.innerWidth)));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const el = ref.current;
    const RO = window.ResizeObserver;

    if (el && typeof RO === 'function') {
      const measure = (width) => {
        if (typeof width === 'number' && width > 0) setNarrow(isNarrowLineWidth(width));
      };
      const ro = new RO((entries) => {
        for (const entry of entries || []) {
          const box = entry && entry.contentRect;
          measure(box && typeof box.width === 'number' ? box.width : el.clientWidth);
        }
      });
      ro.observe(el);
      measure(el.clientWidth);
      return () => ro.disconnect();
    }

    const onResize = () => setNarrow(isNarrowLineWidth(window.innerWidth));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [ref]);

  return narrow;
}

/**
 * @param {'invoice'|'estimate'} kind
 * @param {object|null} doc            the server row (+items, +linked_jobs, +photos) or null for new
 * @param {Array} clients              GET /api/clients
 * @param {Array} availableJobs        GET /api/{kind}s/available-jobs
 * @param {(jobId:number)=>Promise} previewJob   estimates only — GET job-details/:id
 * @param {number} savedAt             bumped by the screen when a SAVE landed
 */
export function DocumentEditor({ kind, doc, isNew = false, clients = [], availableJobs = [], previewJob, canEdit = true, saving = false, savedAt = 0, onSave }) {
  const [form, setForm] = useState(() => formFromDoc(doc));
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState({});
  const [pickJob, setPickJob] = useState('');
  const [jobClient, setJobClient] = useState(null);
  const [previews, setPreviews] = useState({});
  const [previewError, setPreviewError] = useState(null);
  const linesRef = useRef(null);
  const narrowLines = useNarrowLines(linesRef);

  /**
   * ── When the form is (re)loaded from the server, and when it is NOT ───────
   *
   * This was `[doc]` — the react-query object — and that made any refetch this
   * screen caused, for any reason at all, re-derive the whole form. Measured
   * in a browser at 390px on 2026-09-13: typed a new description into Line 1,
   * attached one photo with the screen's own Attach photos control, and the
   * field came back reading the old text with `data-dirty` flipped to "false"
   * and "Photo attached." in the green success notice. The edit was gone and
   * the only sign it had ever existed went with it. Removing a photo did the
   * same. Neither request touches a line item — InvoiceDetail.writePhotos PUTs
   * `{ photos }` and nothing else — so nothing was even in conflict; the form
   * was thrown away because a new OBJECT arrived.
   *
   * The reload itself has to stay: after a save the server normalises (trims
   * descriptions, renumbers sort_order, zeroes the document discount under the
   * XOR) and the form must show what was actually stored. So the trigger is
   * now the two things that genuinely mean "load this document":
   *
   *   docId    a DIFFERENT document — including null -> the row just created
   *   savedAt  a save from THIS form landed; the screens bump it after the PUT
   *
   * `docRef` is what lets the reset read the CURRENT doc without depending on
   * its identity. It is synced in its own effect rather than during render so
   * nothing is written while rendering; effects run in declaration order, so
   * it is always up to date by the time the reset below runs.
   */
  const docRef = useRef(doc);
  useEffect(() => { docRef.current = doc; });
  const docId = doc ? doc.id : null;
  useEffect(() => {
    setForm(formFromDoc(docRef.current));
    setDirty(false);
    setErrors({});
  }, [docId, savedAt]);

  const set = (patch) => { setForm((f) => ({ ...f, ...(typeof patch === 'function' ? patch(f) : patch) })); setDirty(true); };
  const setItem = (key, patch) => set((f) => ({ items: f.items.map((it) => (it.key === key ? { ...it, ...patch } : it)) }));
  const removeItem = (key) => set((f) => ({ items: f.items.filter((it) => it.key !== key) }));
  const moveItem = (key, dir) => set((f) => {
    const i = f.items.findIndex((it) => it.key === key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= f.items.length) return {};
    const items = f.items.slice();
    const [row] = items.splice(i, 1);
    items.splice(j, 0, row);
    return { items };
  });

  const lineMode = anyLineDiscount(form.items);
  const docMode = (Number(form.discount_amount) || 0) > 0;
  const ro = !canEdit;

  const clientsById = useMemo(() => new Map((clients || []).map((c) => [String(c.id), c])), [clients]);
  const onClient = (e) => {
    const id = e.target.value;
    const c = clientsById.get(id);
    set(c
      ? { client_id: id, client_name: c.name || '', client_email: c.email || '', client_phone: c.phone || '', client_address: c.address || '' }
      : { client_id: '' });
    // Link jobs follows the new client again, and a job picked for the old
    // one is not left waiting behind the Link button.
    setJobClient(null);
    setPickJob('');
  };

  const linkedRows = useMemo(() => {
    const byId = new Map();
    (availableJobs || []).forEach((j) => byId.set(Number(j.id), j));
    (doc && Array.isArray(doc.linked_jobs) ? doc.linked_jobs : []).forEach((j) => {
      const id = Number(j.job_id !== undefined ? j.job_id : j.id);
      if (!byId.has(id)) byId.set(id, { id, title: j.title, client_name: j.client_name, property_name: j.property_name, worker_name: j.worker_name });
    });
    return form.job_ids.map((id) => byId.get(Number(id)) || { id, title: `Job #${id}` });
  }, [form.job_ids, availableJobs, doc]);

  const existingIds = useMemo(() => new Set((doc && Array.isArray(doc.linked_jobs) ? doc.linked_jobs : []).map((j) => Number(j.job_id !== undefined ? j.job_id : j.id))), [doc]);
  const unlinked = (availableJobs || []).filter((j) => !form.job_ids.includes(Number(j.id)));
  const link = linkChoices({ jobs: unlinked, docClientId: form.client_id, docClientName: form.client_name, chosen: jobClient });

  const addJob = async () => {
    const id = Number(pickJob);
    if (!Number.isFinite(id) || form.job_ids.includes(id)) return;
    set((f) => ({ job_ids: [...f.job_ids, id] }));
    setPickJob('');
    if (kind === 'estimate' && previewJob) {
      setPreviewError(null);
      try {
        const details = await previewJob(id);
        setPreviews((p) => ({ ...p, [id]: details }));
      } catch (e) {
        setPreviewError(e);
      }
    }
  };
  const removeJob = (id) => set((f) => ({ job_ids: f.job_ids.filter((x) => Number(x) !== Number(id)) }));

  const submit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const errs = validateForm(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (onSave) onSave(payloadFromForm(form, kind, { isNew }));
  };

  /**
   * ── The line-item controls, declared ONCE and rendered by both layouts ────
   *
   * A line item is a FORM, and it is the only list in Office whose cells are
   * inputs rather than text. It has two shapes:
   *
   *   >= 768px of container   the seven-column table, exactly as it was
   *   <  768px of container   `of-lines-stack` — one labelled, stacked form
   *                           group per line, every field present
   *
   * The narrow shape used to be RecordList's record CARD, and that is what
   * this replaces. A card holds a title, a subtitle, a badge and at most three
   * facts — six slots for seven controls — so one had to go, and the one that
   * went was `unit`: a phone could not type "hrs". Squeezing an editable grid
   * into a read-oriented card is the wrong trade at any ratio, because the
   * thing that gives way is always a field somebody needed.
   *
   * Every control therefore lives in this object and is rendered twice: one
   * aria-label, one onChange, one disabled rule. That is not tidiness — it is
   * what makes "the phone renders a field it does not wire up" impossible to
   * write here without deleting something both layouts use.
   */
  const ctl = {
    // A box, not a one-line input (owner, 2026-09-26: "make the what was done
    // a bigger field in case I want to put a long checklist of things and
    // want it to be printed properly"). It grows with its lines, up to a
    // screenful, and every place that shows a line keeps the breaks:
    // InvoicePrint (.of-print__desc), PublicInvoice/PublicEstimate
    // (`white-space: pre-line` on the description cell) and the invoice
    // email (routes/invoices.js turns \n into <br>).
    description: (it) => <FwTextarea aria-label="Line description" rows={descriptionRows(it.description)} value={it.description} disabled={ro} onChange={(e) => setItem(it.key, { description: e.target.value })} placeholder="What was done — a sentence, or a checklist, one item per line" />,
    notes: (it, rows) => <FwTextarea aria-label="Line notes" rows={rows} value={it.notes} disabled={ro} onChange={(e) => setItem(it.key, { notes: e.target.value })} placeholder="Notes shown under this line (optional)" />,
    quantity: (it) => <FwInput aria-label="Quantity" type="number" min="0" step="0.25" inputMode="decimal" value={it.quantity} disabled={ro} onChange={(e) => setItem(it.key, { quantity: e.target.value })} className="of-lines__num" />,
    unit: (it) => <FwInput aria-label="Unit" value={it.unit} disabled={ro} onChange={(e) => setItem(it.key, { unit: e.target.value })} placeholder="hrs" />,
    unitPrice: (it) => <FwInput aria-label="Unit price" type="number" min="0" step="0.01" inputMode="decimal" value={it.unit_price} disabled={ro} onChange={(e) => setItem(it.key, { unit_price: e.target.value })} className="of-lines__num" placeholder="0.00" />,
    discountAmount: (it) => <FwInput aria-label="Line discount amount" type="number" min="0" step="0.01" inputMode="decimal" value={it.discount_amount} disabled={ro || docMode} onChange={(e) => setItem(it.key, { discount_amount: e.target.value })} className="of-lines__num" placeholder="0" />,
    discountType: (it) => (
      <FwSelect aria-label="Line discount type" value={it.discount_type} disabled={ro || docMode} onChange={(e) => setItem(it.key, { discount_type: e.target.value })}>
        <option value="fixed">$</option>
        <option value="percent">%</option>
      </FwSelect>
    ),
    moveUp: (it, i) => <button type="button" className="of-iconbtn" aria-label="Move line up" disabled={ro || i === 0} onClick={() => moveItem(it.key, -1)}>↑</button>,
    moveDown: (it, i) => <button type="button" className="of-iconbtn" aria-label="Move line down" disabled={ro || i === form.items.length - 1} onClick={() => moveItem(it.key, 1)}>↓</button>,
    remove: (it) => <button type="button" className="of-iconbtn of-iconbtn--stop" aria-label="Remove line" disabled={ro || form.items.length === 1} onClick={() => removeItem(it.key)}>×</button>,
    needsPrice: (it) => (needsPrice(it) ? <Pill tone="warn" className="of-needs-price">Needs a price</Pill> : null),
    photos: (it) => (it.photos && it.photos.length ? <span className="of-lines__photos">{it.photos.length} photo{it.photos.length === 1 ? '' : 's'} attached</span> : null),
  };

  /**
   * The wide layout: the table, unchanged.
   *
   * `role` is RecordList's CARD hint and now survives only as a fallback. This
   * branch renders when our own ResizeObserver says the region is >= 768px,
   * and DataTable then measures the same box and agrees — so RecordList's card
   * path is unreachable from this screen. If some future container makes the
   * two disagree, these roles keep the money columns on screen instead of the
   * first three columns in file order; they are not how the phone gets its
   * fields any more.
   */
  const columns = [
    { key: 'order', label: '', width: 72, role: 'hidden', render: (it, i) => <span className="of-lines__order">{ctl.moveUp(it, i)}{ctl.moveDown(it, i)}</span> },
    {
      key: 'description', label: 'Description', role: 'title',
      render: (it) => (
        <div className="of-lines__desc">
          {ctl.description(it)}
          {ctl.notes(it, 1)}
          {ctl.photos(it)}
        </div>
      ),
    },
    { key: 'quantity', label: 'Qty', align: 'right', width: 96, role: 'fact', render: (it) => ctl.quantity(it) },
    // `hidden` only decides what a card would drop if one were ever drawn, and
    // a card with three slots should spend them on the three money columns.
    // The Unit control itself is rendered by BOTH layouts, from ctl.unit.
    { key: 'unit', label: 'Unit', width: 96, role: 'hidden', render: (it) => ctl.unit(it) },
    {
      key: 'unit_price', label: 'Unit price', align: 'right', width: 150, role: 'fact',
      render: (it) => <div className="of-lines__price">{ctl.unitPrice(it)}{ctl.needsPrice(it)}</div>,
    },
    {
      key: 'discount', label: 'Line discount', width: 170, role: 'fact',
      render: (it) => <div className="of-lines__discount">{ctl.discountAmount(it)}{ctl.discountType(it)}</div>,
    },
    { key: 'remove', label: '', width: 56, role: 'badge', render: (it) => ctl.remove(it) },
  ];

  /**
   * The narrow layout: one stacked form group per line.
   *
   * Mobile-first in the sense APP_REBUILD.md §6 means it — no fixed widths, no
   * media query, no minimum a 320px screen cannot meet. The order mirrors the
   * table's own grouping (the description cell carries the notes with it), and
   * the three numeric fields share a wrapping row so the last one to wrap
   * grows to the full width instead of sitting in a half-empty track.
   *
   * Reordering is here too. `sort_order` is written from the array position on
   * every save, so a line a phone cannot move is a line whose position only a
   * desktop can fix.
   */
  const lineStack = (
    <ul className="of-lines-stack" data-layout="stack" aria-label="Line items">
      {form.items.map((it, i) => (
        <li key={it.key} className="of-lines__line" data-line-index={i}>
          <div className="of-lines__linehead">
            <span className="of-lines__n">Line {i + 1}</span>
            <span className="of-lines__acts">
              {ctl.moveUp(it, i)}
              {ctl.moveDown(it, i)}
              {ctl.remove(it)}
            </span>
          </div>
          <label className="of-lines__field">
            <span className="of-lines__cap">Description</span>
            {ctl.description(it)}
          </label>
          <label className="of-lines__field">
            <span className="of-lines__cap">Notes</span>
            {ctl.notes(it, 2)}
          </label>
          <div className="of-lines__cols">
            <label className="of-lines__field">
              <span className="of-lines__cap">Qty</span>
              {ctl.quantity(it)}
            </label>
            <label className="of-lines__field">
              <span className="of-lines__cap">Unit</span>
              {ctl.unit(it)}
            </label>
            <label className="of-lines__field">
              <span className="of-lines__cap">Unit price</span>
              {ctl.unitPrice(it)}
            </label>
          </div>
          {ctl.needsPrice(it)}
          <div className="of-lines__field">
            <span className="of-lines__cap">Line discount</span>
            <div className="of-lines__discount">
              {ctl.discountAmount(it)}
              {ctl.discountType(it)}
            </div>
          </div>
          {ctl.photos(it)}
        </li>
      ))}
    </ul>
  );

  return (
    <form className="of-editor" onSubmit={submit} noValidate data-dirty={dirty ? 'true' : 'false'}>
      <div className="of-editor__grid">
        <section className="of-card">
          <SectionLabel>Client</SectionLabel>
          <FwField label="Client" error={errors.client_name} hint={!errors.client_name && form.client_id ? 'Contact details below were copied from the client record.' : undefined}>
            <FwSelect value={form.client_id} disabled={ro} onChange={onClient} data-field="client_id">
              <option value="">— Choose a client —</option>
              {(clients || []).map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </FwSelect>
          </FwField>
          <div className="of-editor__two">
            <FwField label="Billed to (name)" required>
              <FwInput value={form.client_name} disabled={ro} onChange={(e) => set({ client_name: e.target.value })} data-field="client_name" />
            </FwField>
            <FwField label="Email" error={errors.client_email}>
              <FwInput type="email" value={form.client_email} disabled={ro} onChange={(e) => set({ client_email: e.target.value })} data-field="client_email" />
            </FwField>
            <FwField label="Phone">
              <FwInput value={form.client_phone} disabled={ro} onChange={(e) => set({ client_phone: e.target.value })} data-field="client_phone" />
            </FwField>
            <FwField label="Address">
              <FwInput value={form.client_address} disabled={ro} onChange={(e) => set({ client_address: e.target.value })} data-field="client_address" />
            </FwField>
          </div>
        </section>

        <section className="of-card">
          <SectionLabel>{kind === 'estimate' ? 'Estimate' : 'Invoice'}</SectionLabel>
          {kind === 'estimate' ? (
            <FwField label="Title" hint="Optional. Shown at the top of the estimate.">
              <FwInput value={form.title} disabled={ro} onChange={(e) => set({ title: e.target.value })} data-field="title" />
            </FwField>
          ) : null}
          <FwField label="Description">
            <FwTextarea rows={2} value={form.description} disabled={ro} onChange={(e) => set({ description: e.target.value })} data-field="description" />
          </FwField>
          <div className="of-editor__two">
            {kind === 'invoice' ? (
              <FwField label="Due date">
                <FwInput type="date" value={form.due_date} disabled={ro} onChange={(e) => set({ due_date: e.target.value })} data-field="due_date" />
              </FwField>
            ) : null}
            {kind === 'invoice' ? (
              <FwField label="Payment terms">
                <FwInput value={form.payment_terms} disabled={ro} onChange={(e) => set({ payment_terms: e.target.value })} placeholder="e.g. Net 30" data-field="payment_terms" />
              </FwField>
            ) : null}
            {/* DEPOSIT. The hint below is a claim about what the SERVER does
                with this column, so it is checked against the server:
                server/utils/__tests__/depositWording.test.js reads both charge
                paths in routes/invoices.js and the deposit row in
                PublicInvoice.js, and refuses a sentence that disagrees with
                either.

                It read "Recorded on the invoice; the client is billed the
                balance." until 2026-09-14, and that second clause was false.
                `deposit_amount` is written to its own column and read by
                nothing in the billing path — both POST /invoices/:id/send and
                POST /invoices/public/:token/pay charge `total - amount_paid`,
                and `amount_paid` is a different column that typing a deposit
                does not touch.

                WHETHER a deposit should reduce the charge is a money decision
                the owner has not made (CLAUDE.md rule 4), so the words were
                corrected and the billing was not.

                This comment sits OUTSIDE the ternary on purpose. Inside those
                parentheses it is a second expression beside the element, which
                is a parse error — and one that BOTH the server suite and this
                field's own text-reading guard sail straight past, because
                neither of them parses JSX. It broke 28 client suites. */}
            {kind === 'invoice' ? (
              <FwField label="Deposit ($)" hint="Shown on the client's invoice as 'Deposit requested'. It does not reduce the amount billed; the payment link charges the total less any payment already recorded.">
                <FwInput type="number" min="0" step="0.01" inputMode="decimal" value={form.deposit_amount} disabled={ro} onChange={(e) => set({ deposit_amount: e.target.value })} data-field="deposit_amount" />
              </FwField>
            ) : null}
            {kind === 'estimate' && isNew ? (
              <FwField label="Valid for (days)" hint="Blank = 30 days from when it is sent.">
                <FwInput type="number" min="1" max="365" step="1" inputMode="numeric" value={form.expires_in_days} disabled={ro} onChange={(e) => set({ expires_in_days: e.target.value })} data-field="expires_in_days" />
              </FwField>
            ) : null}
            <FwField
              label="Tax rate (%)"
              hint={doc && doc.tax_rate !== undefined && doc.tax_rate !== null
                ? `Currently ${fmtPct(doc.tax_rate)}. Leave blank to keep it.`
                : "Blank = the client's default rate."}
            >
              <FwInput type="number" min="0" step="0.01" inputMode="decimal" value={form.tax_rate} disabled={ro} onChange={(e) => set({ tax_rate: e.target.value })} placeholder={doc && doc.tax_rate !== undefined && doc.tax_rate !== null ? String(doc.tax_rate) : 'client default'} data-field="tax_rate" />
            </FwField>
          </div>
        </section>
      </div>

      <section className="of-card">
        <div className="of-card__head">
          <SectionLabel>Line items</SectionLabel>
          {!ro ? <FwButton variant="ghost" icon="plus" onClick={() => set((f) => ({ items: [...f.items, blankItem()] }))}>Add line</FwButton> : null}
        </div>
        {errors.items ? <Notice tone="stop">{errors.items}</Notice> : null}
        {docMode ? <Notice tone="info">A whole-{kind} discount is set below, so per-line discounts are off — the server keeps only one of the two.</Notice> : null}
        {/* This wrapper is the box that gets measured, and it stays mounted
            across the swap, so the observer is attached once and never has to
            be re-attached to whichever layout is showing. */}
        <div className="of-lines-region" ref={linesRef} data-lines={narrowLines ? 'stack' : 'table'}>
          {narrowLines ? lineStack : <DataTable className="of-lines" columns={columns} rows={form.items} rowKey="key" caption="Line items" dense />}
        </div>
      </section>

      <div className="of-editor__grid">
        <section className="of-card">
          <SectionLabel>Whole-{kind} discount</SectionLabel>
          {lineMode ? <Notice tone="info">Per-line discounts are in use, so this one is off — the server keeps only one of the two. Clear the line discounts to use it.</Notice> : null}
          <div className="of-editor__two">
            <FwField label="Discount">
              <FwInput type="number" min="0" step="0.01" inputMode="decimal" value={form.discount_amount} disabled={ro || lineMode} onChange={(e) => set({ discount_amount: e.target.value })} data-field="discount_amount" />
            </FwField>
            <FwField label="Type">
              <FwSelect value={form.discount_type} disabled={ro || lineMode} onChange={(e) => set({ discount_type: e.target.value })} data-field="discount_type">
                <option value="fixed">Fixed ($)</option>
                <option value="percent">Percent (%)</option>
              </FwSelect>
            </FwField>
          </div>
          <FwField label="Reason" hint="Shown to the client next to the discount.">
            <FwInput value={form.discount_description} disabled={ro || lineMode} onChange={(e) => set({ discount_description: e.target.value })} data-field="discount_description" />
          </FwField>
          <FwField label="Notes">
            <FwTextarea rows={3} value={form.notes} disabled={ro} onChange={(e) => set({ notes: e.target.value })} data-field="notes" />
          </FwField>
        </section>

        <section className="of-card">
          <SectionLabel>Link jobs</SectionLabel>
          {kind === 'estimate' ? (
            <label className="of-check">
              <input type="checkbox" checked={!!form.pull_job_content} disabled={ro} onChange={(e) => set({ pull_job_content: e.target.checked })} data-field="pull_job_content" />
              <span>Bring the job's photos and work onto the estimate</span>
            </label>
          ) : null}
          {!ro && (link.options.length || link.noClient) ? (
            <FwField label="Jobs for">
              <FwSelect value={link.jobsFor} onChange={(e) => { setJobClient(e.target.value); setPickJob(''); }} data-field="pick_job_client">
                <option value="">Any client</option>
                {link.options.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                {link.noClient ? <option value="none">{NO_CLIENT}</option> : null}
              </FwSelect>
            </FwField>
          ) : null}
          {!ro ? (
            <div className="of-editor__pick">
              <FwField label="Add a job">
                <FwSelect value={pickJob} onChange={(e) => setPickJob(e.target.value)} data-field="pick_job">
                  {/* "No jobs to link for this client" only when OTHER clients
                      have some — so it is a fact about this client. An empty
                      list overall may be a failed read (the screen passes []
                      for both, and says so in its own banner), so it keeps the
                      neutral wording it always had (#711 review). */}
                  <option value="">{link.shown.length || !(availableJobs || []).length ? '— Choose a job —' : '— No jobs to link for this client —'}</option>
                  {link.groups
                    ? link.groups.map((g) => (
                      <optgroup key={g.key} label={g.name}>
                        {g.jobs.map((j) => <option key={j.id} value={String(j.id)}>{jobInClient(j)}</option>)}
                      </optgroup>
                    ))
                    : link.shown.map((j) => <option key={j.id} value={String(j.id)}>{jobInClient(j)}</option>)}
                </FwSelect>
              </FwField>
              <FwButton variant="ghost" icon="link" onClick={addJob} disabled={!pickJob}>Link</FwButton>
            </div>
          ) : null}
          {previewError ? <Notice tone="warn">Could not preview that job. It is still linked.</Notice> : null}
          {linkedRows.length === 0 ? <p className="of-muted">No jobs linked.</p> : (
            <ul className="of-linked">
              {linkedRows.map((j) => (
                <li key={j.id} className="of-linked__row" data-job-id={j.id}>
                  <div className="of-linked__main">
                    <span className="of-linked__title">{jobLabel(j)}</span>
                    {j.worker_name ? <span className="of-muted"> · {j.worker_name}</span> : null}
                    {kind === 'estimate' && !existingIds.has(Number(j.id)) && previews[j.id] ? (
                      <Notice tone="info" className="of-linked__preview" data-preview="true">
                        {form.pull_job_content ? describePull(previews[j.id]) : 'This will link the job only — the box above is unticked, so nothing else comes across.'}
                      </Notice>
                    ) : null}
                  </div>
                  {!ro ? <button type="button" className="of-iconbtn of-iconbtn--stop" aria-label={`Unlink ${j.title}`} onClick={() => removeJob(j.id)}>×</button> : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="of-editor__foot">
        <div className="of-editor__totals">
          <SectionLabel>Totals</SectionLabel>
          <TotalsPanel doc={doc} kind={kind} pending={isNew || dirty || !doc} />
        </div>
        {!ro ? (
          <div className="of-editor__save">
            {dirty ? <span className="of-muted">Unsaved changes</span> : null}
            <FwButton type="submit" variant="primary" loading={saving} icon="check">{isNew ? `Create ${kind}` : 'Save'}</FwButton>
          </div>
        ) : null}
      </div>
    </form>
  );
}

export default DocumentEditor;
